<template>
  <!-- Literal modifier classes rather than an interpolated
       `sparkit-logo--${variant}`. nuxt4 does not run PurgeCSS today, so this is
       not load-bearing here; it is how nuxt2 had to write it (PurgeCSS scans
       for literal class names and dropped both modifier blocks), and keeping
       the form costs nothing. AB#62665 -->
  <NuxtLink
    v-if="isVisible"
    :to="SPARKIT_ROOT"
    class="sparkit-logo"
    :class="{
      'sparkit-logo--desktop': variant === 'desktop',
      'sparkit-logo--menu': variant === 'menu',
    }"
    :data-testid="testId"
    aria-label="Sparkit"
    @click="emits('close')"
  >
    <span class="sparkit-logo__frame">
      <img
        src="~/assets/images/Sparkit_Logo_Black.svg"
        alt="Sparkit"
        class="sparkit-logo__image sparkit-logo__image--black"
      >
      <!-- The colour swap and 40% enlargement belong to the TopNav logo alone —
           design specified no hover or active state on the menu surfaces, so the
           menu variant has no hover artwork to render. AB#62633 -->
      <img
        v-if="variant === 'desktop'"
        src="~/assets/images/Sparkit_Logo_Color.svg"
        alt=""
        aria-hidden="true"
        class="sparkit-logo__image sparkit-logo__image--color"
      >
    </span>
  </NuxtLink>
</template>

<script setup lang="ts">
import type { CategoryItem } from '~/types/root'
import { UserRole } from '~/types/account'
import { isSparkitCategorySlug, SPARKIT_ROOT } from '~/utils/categoryRoutes'

type SparkitLogoVariant = 'desktop' | 'menu'

interface Props {
  categories: CategoryItem[]
  variant?: SparkitLogoVariant
  testId?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'desktop',
  testId: 'sparkit-logo',
})

const emits = defineEmits<{
  (event: 'close'): void
}>()

const { userRole, isDemo } = useSAuthState()
const visibleRoles = new Set([UserRole.Customer, UserRole.Guest, UserRole.Anonymous, UserRole.Agent])

// Sitefinity authors the department slug with its `~` site-root marker, so the
// node arrives as `~/shop-products/sparkit` while SPARKIT_CATEGORY — which route
// matching relies on — stays `/shop-products/sparkit`. AB#62408
const containsSparkit = (categories: CategoryItem[]): boolean =>
  categories.some((category) =>
    isSparkitCategorySlug(category.slug) || containsSparkit(category.children ?? []),
  )

// The TopNav logo is customer-only (59951), so the desktop variant keeps the
// role gate. The Shop dropdown and the mobile drawer both use the menu variant
// and are shown to demonstrators as well, so that variant has no gate. AB#62633
const isVisible = computed(() =>
  (props.variant === 'menu' || (!isDemo.value && visibleRoles.has(userRole.value)))
  && containsSparkit(props.categories),
)
</script>

<style scoped lang="scss">
.sparkit-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  vertical-align: middle;

  &__frame {
    position: relative;
    display: block;
    width: 72px;
    height: 20px;
  }

  &__image {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    transform-origin: center;
  }

  &__image--color {
    opacity: 0;
  }

  // The colour swap and 40% enlargement belong to the TopNav logo alone —
  // design specified no hover or active state on the menu surfaces. AB#62633
  &--desktop {
    margin-left: -8px;

    &:hover,
    &:focus-visible {
      .sparkit-logo__image--black {
        opacity: 0;
      }

      .sparkit-logo__image--color {
        opacity: 1;
        transform: scale(1.2);
      }
    }
  }

  &--menu {
    width: 100%;
    justify-content: flex-start;
    padding: 12px 24px;
    border-top: 1px solid rgb(var(--v-theme-backgroundButton));

    .sparkit-logo__frame {
      width: 100px;
      height: 42px;
    }

    // No hover treatment here, but keyboard focus still needs to be visible.
    &:focus-visible {
      outline: 2px solid currentcolor;
      outline-offset: 2px;
    }
  }
}
</style>
